import { Howl } from 'howler'
import { IAudioPlayerService } from './IAudioPlayerService'
import { audioSettings } from '../AudioSettings/AudioSettings'

const MUTED_DWELL_MS = 800
const WATCHDOG_MS = 20000

/**
 * Plays one sound at a time and resolves when playback finishes.
 * Sequencing across sounds/speech is handled by AudioQueueManager.
 * Never rejects: a missing or broken sound logs and resolves so the
 * game keeps moving.
 */
export class AudioPlayerService implements IAudioPlayerService {
  public isInitialized = false

  public init() {
    this.isInitialized = true
  }

  public async playSound(src: string): Promise<void> {
    if (!this.isInitialized) {
      console.warn('AudioPlayer not initialized, skipping sound')
      return
    }

    if (audioSettings.isMuted()) {
      // Keep the dramatic pacing without the noise.
      await new Promise((resolve) => setTimeout(resolve, MUTED_DWELL_MS))
      return
    }

    await new Promise<void>((resolve) => {
      let settled = false
      const sound: Howl = new Howl({
        src: [src],
        html5: true,
        volume: 1.0,
      })

      const finish = () => {
        if (settled) return
        settled = true
        clearTimeout(watchdog)
        try {
          sound.unload()
        } catch {
          // ignore unload errors
        }
        resolve()
      }

      // A sound that never loads or never ends must not freeze the game.
      const watchdog = setTimeout(() => {
        console.warn(`Audio watchdog fired for ${src}`)
        finish()
      }, WATCHDOG_MS)

      sound.once('end', finish)
      sound.once('loaderror', (_id, error) => {
        console.error(`Audio failed to load: ${src}`, error)
        finish()
      })
      sound.once('playerror', (_id, error) => {
        console.error(`Audio failed to play: ${src}`, error)
        finish()
      })

      sound.play()
    })
  }
}
